import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../../img/logo.jpeg';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Technology', href: '/technology' },
  ];

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <header
      className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'glass shadow-glass py-2'
          : 'bg-white/60 backdrop-blur-sm py-3'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="absolute inset-0 bg-accent/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <img
                src={Logo}
                alt="OffshoreBrücke Logo"
                className="w-11 h-11 object-contain rounded-xl relative z-10 transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <span className="text-xl font-bold text-primary tracking-tight">
              OffshoreBrücke
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                  isActive(item.href) 
                    ? 'text-primary bg-primary/8' 
                    : 'text-textLight hover:text-primary hover:bg-primary/5'
                }`}
              >
                {item.name}
                {isActive(item.href) && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-accent rounded-full"></span>
                )}
              </Link>
            ))}

            <div className="w-px h-6 bg-border mx-3"></div>

            <a
              href="https://calendly.com/offshorebruecke/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 text-sm font-semibold text-primary border border-primary/20 rounded-xl hover:bg-primary/5 hover:border-primary/40 transition-all duration-300"
            >
              Book a Call
            </a>
            <Link
              to="/login"
              className="px-5 py-2.5 text-sm font-semibold text-white bg-primary rounded-xl hover:bg-primary-light shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ml-2"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-primary/5 transition-colors duration-300"
            aria-label="Toggle menu"
          >
            <div className="relative w-5 h-4">
              <span className={`absolute left-0 w-5 h-0.5 bg-primary rounded-full transition-all duration-300 ${isMenuOpen ? 'top-1.5 rotate-45' : 'top-0'}`}></span>
              <span className={`absolute left-0 top-1.5 w-5 h-0.5 bg-primary rounded-full transition-all duration-300 ${isMenuOpen ? 'opacity-0 scale-0' : 'opacity-100'}`}></span>
              <span className={`absolute left-0 w-5 h-0.5 bg-primary rounded-full transition-all duration-300 ${isMenuOpen ? 'top-1.5 -rotate-45' : 'top-3'}`}></span>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden overflow-hidden transition-all duration-400 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
          <div className="pb-4 space-y-1 border-t border-border/50 pt-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-all duration-300 ${
                  isActive(item.href) 
                    ? 'text-primary bg-primary/8' 
                    : 'text-textLight hover:text-primary hover:bg-primary/5'
                }`}
              >
                {item.name}
              </Link>
            ))}

            <div className="pt-3 space-y-2">
              <a
                href="https://calendly.com/offshorebruecke/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-6 py-3 text-sm font-semibold text-primary border border-primary/20 rounded-xl hover:bg-primary/5 transition-all duration-300"
              >
                Book a Call
              </a>
              <Link
                to="/login"
                className="block w-full text-center px-6 py-3 text-sm font-semibold text-white bg-primary rounded-xl hover:bg-primary-light transition-all duration-300"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
